/**
 * The devices present in this browser's World, right now (RFC 0261 slice 2/3).
 *
 * Reads the World pointer, follows it to the presence doc, and projects the live
 * devices through the liveness window — so the composer's placement picker only
 * ever offers a device that is beating (a dead one can't serve a run). Reactive:
 * each heartbeat rewrites the presence doc, so the list refreshes on its own.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import { type DevicePresenceRef, projectPresentDevices } from '@mod/core/model'
import { useMemo } from 'react'
import { useDocument } from './repo/useDocument'
import { readWorldPointer } from './world'

export function useWorldDevices(): DevicePresenceRef[] {
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  const presenceDocId = (worldDoc as { presenceDocId?: string } | undefined)
    ?.presenceDocId
  const [presenceDoc] = useDocument(presenceDocId as AnyDocumentId | undefined)
  return useMemo(
    // Date.now() at render is fine: the presence doc rewrites on every heartbeat,
    // so the window is re-evaluated frequently without a timer of our own.
    () => projectPresentDevices(presenceDoc, Date.now()),
    [presenceDoc],
  )
}
